import QuizBase from 'es6/controllers/apps/quizzes/quiz-base';
import { QuizResult, ResultTypes } from 'es6/models/quiz-result';

/**
 * Base class for the sentence trainer quizzes.
 *
 * Subclasses should implement:
 *
 * _showConfirmationScreen()
 * _showQuizScreen()
 * _showRecallScreen()
 */

const MAX_HINTS = 3;

const MAX_MISTAKES = 3;

export class SentenceQuizBase extends QuizBase {
  constructor(sentence, ...args) {
    super('sentences', sentence.getId(), ...args);
    this._sentence = sentence;

    // Used to determine quiz result.
    this._isCorrect = false;

    // Keep track of hints and mistakes.
    this._hints = 0;
    this._mistakes = 0;

    // Sentence quizzes can have multiple quiz results. Maintain
    // them here.
    this.results = [this.result];
  }

  /**
   * Sentence quizzes return multiple results.
   *
   * @override
   * @returns {QuizResult[]}
   */
  getFinalResult() {
    return this.results.map(result => result.finalize());
  }

  /**
   * Returns the total number of hints allowed.
   *
   * @returns {Number}
   */
  _getMaxHints() {
    return MAX_HINTS;
  }

  /**
   * Returns the total number of mistakes allowed.
   *
   * @returns {Number}
   */
  _getMaxMistakes() {
    return MAX_MISTAKES;
  }

  /**
   * Callback for whenever the user hits the don't know button.
   */
  _handleDontKnow(fillInTheBlankHTML) {
    this.result.beginPhase('confirmation');
    this.result.setResult(ResultTypes.INCORRECT);
    this._fillInTheBlankHTML = fillInTheBlankHTML;
    this._showConfirmationScreen();
  }

  /**
   * Callback for onNext with the recall screen.
   */
  _handleRecallNext() {
    this.result.beginPhase('quiz');
    this._showQuizScreen();
  }

  /**
   * Pushes the current quiz result onto the results array and
   * creates a new quiz result.
   *
   * @param {String} phase      what phase to start the new result on
   */
  _pushQuizResult(phase = 'quiz') {
    this.result.resume();
    this.result.endCurrentPhase();

    const { contentId, contentDomain, goalId, leQuizType } = this.result;

    // Create a new result and start the new phase.
    this.result = new QuizResult(contentDomain, contentId, goalId, leQuizType);
    this.result.beginPhase(phase);
    this.results.push(this.result);
  }

  /**
   * Sets the appropriate quiz result based on the current
   * instance variables.
   */
  _setQuizResult() {
    let resultType;

    if (this._isCorrect) {
      if (this._hints > 0) {
        resultType = ResultTypes.CORRECT_WITH_HINT;
      } else {
        resultType = ResultTypes.CORRECT;
      }
    } else {
      resultType = ResultTypes.INCORRECT;
    }

    this.result.setResult(resultType);
  }

  /**
   * Starts the quiz.
   */
  show() {
    this.result.beginPhase('recall');
    this._showRecallScreen();
  }

  _showConfirmationScreen() {
    throw new Error('_showConfirmationScreen() needs to be overridden');
  }

  _showQuizScreen() {
    throw new Error('_showQuizScreen() needs to be overridden');
  }

  _showRecallScreen() {
    throw new Error('_showRecallScreen() needs to be overridden');
  }
}

export default SentenceQuizBase;
